import { collection, doc, getDoc, getDocs, query, setDoc } from 'firebase/firestore';
import { firestore } from '@api/Firebase/Firebase';
import { Dish, dishesRef } from '@api/Dishes/Dish';
import { stripAuditFields } from '@api/ActivityLogs/ActivityOrigin';
import { Recipe, recipesRef } from '@api/Recipes/Recipe';
import { ModifierGroup, modifierGroupsRef } from '@api/ModifierGroups/ModifierGroup';
import { Input, inputsRef } from '@api/Inputs/Input';
import { ProductCategory, productCategoriesRef } from '@api/ProductCategories/ProductCategory';
import { InputCategory, inputCategoriesRef } from '@api/InputCategories/InputCategory';

// ============================================================================
// EL CATÁLOGO PRE-ARMADO EN **UN** DOCUMENTO — `catalog/snapshot`
//
// Antes: POS, web y server leían las 5 colecciones completas (**336 docs**) y se
// apoyaban en cachés por versión que, medidas el 8-sep, **no bajaron las
// lecturas**: la del server muere con la máquina (Fly escala a cero) y la del
// POS muere en cada recarga de tablet. Ahora hay un documento con todo el
// catálogo ya armado: **1 lectura, siempre, para todos.**
//
// Cabe porque el 2026-09-09 las fotos salieron de los documentos a Storage (F0):
// el catálogo pasó de 1,391 KB a **171 KB**, el 17 % del límite de 1 MiB.
// ⚠️ **Nada que crezca sin techo entra aquí**, y una foto embebida menos que
// nada: `assertSnapshotFits` rechaza la publicación antes de que Firestore la
// rechace a ella.
//
// ## Por qué el consumidor lee SOLO este doc y no también la versión
// El snapshot lleva su `version` DENTRO, así que una sola lectura trae el
// catálogo y el dato para saber si cambió. `counters/catalog-version` se sigue
// escribiendo por compatibilidad con lo que ya está desplegado, pero los
// lectores nuevos no lo necesitan.
//
// ## Orden de escritura, que NO es negociable
// Primero el snapshot, después la versión. Al revés, un fallo entre las dos
// dejaría a los consumidores viendo versión nueva con catálogo viejo, y creyendo
// que están al día. En este orden, un fallo deja versión vieja con snapshot
// nuevo: los consumidores siguen con su caché, que es el estado seguro.
//
// ⚠️ PARIDAD ×3: mismo id de doc, misma forma y misma regla de frescura en
// krispis-web (`api/Catalog/CatalogSnapshot.ts`) y krispis-server
// (`api/Catalog/CatalogSnapshot.ts`). **Solo el admin publica.**
// ============================================================================

export const CATALOG_SNAPSHOT_COLLECTION = 'catalog';
export const CATALOG_SNAPSHOT_DOC_ID = 'snapshot';

/** Techo de aviso. El límite duro de Firestore es 1 MiB; se corta muy antes. */
export const CATALOG_SNAPSHOT_MAX_BYTES = 700 * 1024;

export interface CatalogSnapshot {
    version: number;
    publishedAt: unknown;
    publishedReason: string;
    bytes: number;
    dishes: Dish[];
    recipes: Recipe[];
    modifierGroups: ModifierGroup[];
    inputs: Input[];
    productCategories: ProductCategory[];
}

export const catalogSnapshotRef = doc(collection(firestore, CATALOG_SNAPSHOT_COLLECTION), CATALOG_SNAPSHOT_DOC_ID);

/**
 * Peso serializado en bytes UTF-8, el mismo criterio con el que Firestore mide
 * el documento.
 *
 * Se cuenta a mano y no con `TextEncoder`/`Blob` a propósito: este módulo es
 * réplica de paridad ×3 y corre también en Node (el server) y en el entorno de
 * pruebas, donde `TextEncoder` no siempre existe. Un guard de tamaño que se cae
 * por el entorno no es un guard.
 */
export const getSnapshotBytes = (snapshot: unknown): number => {
    const text = JSON.stringify(snapshot) || '';
    let bytes = 0;
    for (let index = 0; index < text.length; index += 1) {
        const code = text.charCodeAt(index);
        if (code < 0x80) bytes += 1;
        else if (code < 0x800) bytes += 2;
        else if (code >= 0xd800 && code <= 0xdbff) {
            // Par surrogate (emoji): 4 bytes entre los dos code units.
            bytes += 4;
            index += 1;
        } else bytes += 3;
    }
    return bytes;
};

/** ¿Trae un catálogo utilizable? Un doc a medio escribir o vacío NO lo es. */
export const isSnapshotUsable = (snapshot?: Partial<CatalogSnapshot> | null): boolean =>
    !!snapshot &&
    typeof snapshot.version === 'number' &&
    Array.isArray(snapshot.dishes) &&
    Array.isArray(snapshot.recipes) &&
    Array.isArray(snapshot.modifierGroups) &&
    Array.isArray(snapshot.inputs) &&
    Array.isArray(snapshot.productCategories) &&
    snapshot.dishes.length > 0;
// ⚠️ Las tres copias validan las CINCO colecciones. Si una valida menos que otra,
// un snapshot truncado lo acepta una superficie y lo rechaza la otra sobre el
// mismo documento (divergencia detectada en el re-check del 2026-09-09).

/**
 * Corta la publicación si el catálogo ya no cabe con holgura, diciendo qué la
 * infló. Sin esto, el primer aviso llegaría el día que Firestore rechace la
 * escritura y el catálogo se quede sin publicar.
 */
export const assertSnapshotFits = (snapshot: CatalogSnapshot): void => {
    const embebidas = (snapshot.dishes || []).filter((dish) =>
        /^data:/i.test(String((dish as Dish).photoSrc || ''))
    ).length;
    if (embebidas > 0) {
        throw new Error(
            `${embebidas} producto(s) tienen la foto DENTRO del documento. Las fotos van en Storage: vuelve a guardar esos productos (ver api/Catalog/CatalogPhotos.ts).`
        );
    }
    if (snapshot.bytes > CATALOG_SNAPSHOT_MAX_BYTES) {
        throw new Error(
            `El catálogo pesa ${Math.round(
                snapshot.bytes / 1024
            )} KB y el máximo para publicarlo en un documento es ${Math.round(
                CATALOG_SNAPSHOT_MAX_BYTES / 1024
            )} KB. Revisa qué campo creció (scripts/verificar-tamano-catalogo.mjs).`
        );
    }
};

// ⚠️ Se quitan los campos de AUDITORÍA (`createdAt`/`createdBy`, D9 del plan de
// la bitácora): el snapshot es UN documento con 1 MiB de límite y es lo que hace
// que el catálogo cueste una lectura en vez de 336. Quién creó un producto no le
// sirve al POS ni a la web, y son datos del staff en algo que lee cualquier
// visitante. ⚠️ PARIDAD: `scripts/publicar-catalogo.mjs` arma el mismo snapshot.
const readAll = async <T>(ref: typeof dishesRef): Promise<T[]> => {
    const snapshot = await getDocs(query(ref));
    return snapshot.docs.map(
        (document) => ({ ...stripAuditFields(document.data() as object), id: document.id }) as T
    );
};

// ⚠️ **EL SNAPSHOT SE GUARDA YA ORDENADO.** Se ordena UNA vez al publicar, no en
// cada lectura, y así los tres consumidores reciben el mismo orden sin repetir
// la regla.
//
// Esto se olvidó en la primera versión (2026-09-09) y el menú del POS salió
// desordenado en producción: `getDocs` devuelve por id de documento, mientras
// que los servicios que se sustituyeron ordenaban por `priority` y nombre. Las
// categorías se veían "Combos · Bebidas · Desayunos · Papas" en vez de
// "Combos · Paquetes · Bebidas · Complementos", ignorando el orden que el
// negocio configura arrastrando.
const porPrioridadYNombre = <T extends { priority?: number; name?: string }>(items: T[]): T[] =>
    [...items].sort(
        (a, b) => (a.priority || 0) - (b.priority || 0) || (a.name || '').localeCompare(b.name || '', 'es')
    );
const porNombre = <T extends { name?: string }>(items: T[]): T[] =>
    [...items].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es'));

/**
 * Arma el snapshot leyendo las 5 colecciones (**336 lecturas**). Es el precio de
 * publicar, y se paga solo cuando alguien GUARDA algo del catálogo — unas pocas
 * veces al día — para que las decenas de miles de lecturas de la operación
 * cuesten 1 cada una.
 *
 * Los insumos se desnormalizan con su categoría **sin el N+1** de
 * `InputServ.getInputs`: una query de categorías (~10 docs) resuelta en memoria,
 * en vez de un `getDoc` por insumo.
 */
export const buildCatalogSnapshot = async (version: number, reason: string): Promise<CatalogSnapshot> => {
    const [dishes, recipes, modifierGroups, inputs, productCategories, inputCategories] = await Promise.all([
        readAll<Dish>(dishesRef),
        readAll<Recipe>(recipesRef),
        readAll<ModifierGroup>(modifierGroupsRef),
        readAll<Input>(inputsRef),
        readAll<ProductCategory>(productCategoriesRef),
        readAll<InputCategory>(inputCategoriesRef)
    ]);
    const categoriesById = new Map(inputCategories.map((category) => [category.id, category]));
    const inputsWithCategory = inputs.map((input) =>
        input.inputCategoryId
            ? { ...input, inputCategoryData: categoriesById.get(input.inputCategoryId) as InputCategory }
            : input
    );
    const snapshot: CatalogSnapshot = {
        version,
        publishedAt: new Date(),
        publishedReason: reason,
        bytes: 0,
        // El MISMO orden que daban los servicios a los que sustituye — ver arriba.
        dishes: porPrioridadYNombre(dishes),
        recipes: porNombre(recipes),
        modifierGroups: porNombre(modifierGroups),
        inputs: porNombre(inputsWithCategory),
        productCategories: porPrioridadYNombre(productCategories)
    };
    snapshot.bytes = getSnapshotBytes(snapshot);
    return snapshot;
};

/** Publica el snapshot. **Lanza** si no cabe o si trae fotos embebidas. */
export const writeCatalogSnapshot = async (snapshot: CatalogSnapshot): Promise<void> => {
    assertSnapshotFits(snapshot);
    await setDoc(catalogSnapshotRef, snapshot as unknown as Record<string, unknown>);
};

/**
 * El catálogo publicado: **1 lectura**. `undefined` si no hay snapshot usable.
 *
 * ⚠️ **Nunca lanza.** Un fallo de red o de permisos tiene que caer al fallback de
 * las colecciones, no dejar el POS con el menú vacío y una alerta de error: en
 * la primera versión (2026-09-09) el `getDoc` iba sin `try/catch` y la excepción
 * subía hasta la pantalla. La copia de la web sí lo hacía bien; ésta no.
 */
export const readCatalogSnapshot = async (): Promise<CatalogSnapshot | undefined> => {
    try {
        const document = await getDoc(catalogSnapshotRef);
        if (!document.exists()) return undefined;
        const snapshot = document.data() as CatalogSnapshot;
        return isSnapshotUsable(snapshot) ? snapshot : undefined;
    } catch (error) {
        console.error('No se pudo leer el catálogo publicado; se usará el camino de las colecciones', error);
        return undefined;
    }
};
